/**
 * IFC-specific base64 alphabet (22-char GUID encoding).
 * Ported from the C++ generate_IFC_name() in export_IFC.cpp.
 */
const IFC_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_$';

/**
 * Generate a 22-character IFC GloballyUniqueId.
 * Uses the same encoding as the C++ implementation.
 */
export function generateIfcGuid(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // Fallback for Node.js test environment
    for (let i = 0; i < 16; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // Convert 16 bytes (128 bits) to 22 base-64 characters
  // Each group of 3 bytes (24 bits) encodes to 4 base-64 chars (6 bits each)
  // 16 bytes = 5 groups of 3 + 1 remaining byte
  // 5 * 4 + 2 = 22 characters
  const result: string[] = [];

  for (let i = 0; i < 16; i += 3) {
    const remaining = Math.min(3, 16 - i);
    let value = 0;
    for (let j = 0; j < remaining; j++) {
      value = value * 256 + bytes[i + j];
    }

    const chars = remaining === 3 ? 4 : remaining === 2 ? 3 : 2;
    for (let j = 0; j < chars; j++) {
      result.push(IFC_ALPHABET[value % 64]);
      value = Math.floor(value / 64);
    }
  }

  return result.join('');
}

/** Format a string for STEP: wraps in single quotes */
export function stepString(value: string): string {
  return `'${value}'`;
}

/** Format a float for STEP output */
export function stepFloat(value: number): string {
  // Use enough precision, remove trailing zeros but keep at least one decimal
  const str = value.toPrecision(12);
  // Ensure there's a decimal point
  if (!str.includes('.') && !str.includes('E') && !str.includes('e')) {
    return str + '.0';
  }
  return str;
}

/** Format a STEP enum value: .VALUE. */
export function stepEnum(value: string): string {
  return `.${value}.`;
}

/** Format a STEP list: (val1,val2,...) */
export function stepList(values: string[]): string {
  return `(${values.join(',')})`;
}

/**
 * Encode decimal degrees as an IfcCompoundPlaneAngleMeasure STEP list
 * (degrees, minutes, seconds, millionths-of-second) — the form RefLatitude /
 * RefLongitude take. The sign is carried on every component.
 */
export function stepCompoundAngle(decimalDegrees: number): string {
  const sign = decimalDegrees < 0 ? -1 : 1;
  let rest = Math.abs(decimalDegrees);
  const degrees = Math.floor(rest);
  rest = (rest - degrees) * 60;
  const minutes = Math.floor(rest);
  rest = (rest - minutes) * 60;
  const seconds = Math.floor(rest);
  const millionths = Math.round((rest - seconds) * 1e6);
  return stepList([degrees, minutes, seconds, millionths].map((v) => String(sign * v)));
}

/** STEP undefined / optional value */
export const STEP_UNDEFINED = '$';

/** STEP derived value */
export const STEP_DERIVED = '*';
